import { useState } from 'react';
import {
  Plus,
  FileText,
  MoreHorizontal,
  CheckCircle,
  XCircle,
  Send,
  Trash2,
  Package,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  PageHeader,
  TableFilters,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  KPIGrid,
  ConfirmDialog,
} from '@/components/common';
import { PurchaseOrder, QueryPurchaseOrderParams, InventoryItem } from '@/api/services';
import {
  usePurchaseOrdersList,
  useCreatePurchaseOrder,
  useSubmitPurchaseOrder,
  useIssuePurchaseOrder,
  useCancelPurchaseOrder,
  useSuppliersList,
  useInventoryList,
} from '@/api/hooks';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { formatCurrency as formatMoney } from '@/lib/utils';

const statusMap: Record<
  string,
  'draft' | 'pending' | 'approved' | 'rejected' | 'processing' | 'completed'
> = {
  DRAFT: 'draft',
  SUBMITTED: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  ORDERED: 'processing',
  PARTIALLY_RECEIVED: 'processing',
  RECEIVED: 'completed',
  CANCELLED: 'rejected',
};

interface LineItem {
  itemId: string;
  item?: InventoryItem;
  quantity: number;
  unitPrice: number;
}

interface POFormData {
  supplierId: string;
  expectedDate: string;
  currency: string;
  notes: string;
  items: LineItem[];
}

const emptyFormData: POFormData = {
  supplierId: '',
  expectedDate: '',
  currency: 'USD',
  notes: '',
  items: [],
};

export default function PurchaseOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Form state
  const [formOpen, setFormOpen] = useState(false);
  const [formData, setFormData] = useState<POFormData>(emptyFormData);

  // Action dialog
  const [actionDialog, setActionDialog] = useState<{
    open: boolean;
    order?: PurchaseOrder;
    action?: 'submit' | 'issue' | 'cancel';
  }>({ open: false });

  // Build query params
  const queryParams: QueryPurchaseOrderParams = {
    ...(searchQuery && { search: searchQuery }),
    ...(statusFilter && { status: statusFilter as PurchaseOrder['status'] }),
  };

  // React Query hooks for data fetching
  const { data: ordersData, isLoading: loading } = usePurchaseOrdersList(queryParams);
  const { data: suppliersData, isLoading: loadingSuppliers } = useSuppliersList({
    isActive: true,
    limit: 500,
  });
  const { data: inventoryData, isLoading: loadingItems } = useInventoryList({ limit: 500 });

  // Derived data from queries
  const orders = ordersData?.data ?? [];
  const suppliers = suppliersData?.data ?? [];
  const inventoryItems = inventoryData?.data ?? [];

  // Mutations
  const createMutation = useCreatePurchaseOrder();
  const submitMutation = useSubmitPurchaseOrder();
  const issueMutation = useIssuePurchaseOrder();
  const cancelMutation = useCancelPurchaseOrder();

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /**
   * Delegates to the shared formatter so the currency comes from the record
   * rather than from a hardcoded 'USD' in this file. A page-local formatter is
   * how an order billed in dirhams ends up rendered with a dollar sign.
   */
  const formatCurrency = (amount?: number | null, currency = 'USD') =>
    amount == null ? '-' : formatMoney(amount, currency);

  const openCreateForm = () => {
    setFormData(emptyFormData);
    setFormOpen(true);
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { itemId: '', quantity: 1, unitPrice: 0 }],
    });
  };

  const handleRemoveItem = (index: number) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const handleItemChange = (index: number, field: keyof LineItem, value: string | number) => {
    const newItems = [...formData.items];
    if (field === 'itemId') {
      const selectedItem = inventoryItems.find((item) => item.id === value);
      newItems[index] = {
        ...newItems[index],
        itemId: value as string,
        item: selectedItem,
        unitPrice: selectedItem?.unitCost || 0,
      };
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setFormData({ ...formData, items: newItems });
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.supplierId) {
      toast.error('Please select a supplier');
      return;
    }

    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    const invalidItems = formData.items.filter((item) => !item.itemId || item.quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error('Please complete all line items');
      return;
    }

    createMutation.mutate(
      {
        companyId: user?.companyId || '',
        supplierId: formData.supplierId,
        expectedDate: formData.expectedDate || undefined,
        currency: formData.currency,
        notes: formData.notes || undefined,
        items: formData.items.map((item) => ({
          itemId: item.itemId,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
        })),
      },
      {
        onSuccess: () => {
          toast.success('Purchase order created successfully');
          setFormOpen(false);
        },
      }
    );
  };

  const handleAction = async () => {
    if (!actionDialog.order || !actionDialog.action) return;

    const orderId = actionDialog.order.id;
    const onSuccess = () => {
      setActionDialog({ open: false });
    };

    switch (actionDialog.action) {
      case 'submit':
        submitMutation.mutate(orderId, {
          onSuccess: () => {
            toast.success('PO submitted for approval');
            onSuccess();
          },
        });
        break;
      case 'issue':
        issueMutation.mutate(orderId, {
          onSuccess: () => {
            toast.success('PO issued to supplier');
            onSuccess();
          },
        });
        break;
      case 'cancel':
        cancelMutation.mutate(orderId, {
          onSuccess: () => {
            toast.success('PO cancelled');
            onSuccess();
          },
        });
        break;
    }
  };

  const getActionDialogContent = () => {
    switch (actionDialog.action) {
      case 'submit':
        return {
          title: 'Submit for Approval',
          description: 'This will send the purchase order for approval. Are you sure?',
          confirmLabel: 'Submit',
          variant: 'default' as const,
        };
      case 'issue':
        return {
          title: 'Issue to Supplier',
          description: 'This will issue the purchase order to the supplier. Are you sure?',
          confirmLabel: 'Issue',
          variant: 'default' as const,
        };
      case 'cancel':
        return {
          title: 'Cancel Purchase Order',
          description: 'This will cancel the purchase order. This action cannot be undone.',
          confirmLabel: 'Cancel PO',
          variant: 'destructive' as const,
        };
      default:
        return { title: '', description: '', confirmLabel: '', variant: 'default' as const };
    }
  };

  // Calculate metrics
  const totalValue = orders.reduce((sum, order) => sum + (order.totalAmount || 0), 0);
  const pendingOrders = orders.filter((o) => o.status === 'SUBMITTED').length;
  const orderedCount = orders.filter((o) =>
    ['ORDERED', 'PARTIALLY_RECEIVED'].includes(o.status)
  ).length;

  const columns: Column<PurchaseOrder>[] = [
    {
      key: 'poNumber',
      linkTo: (order) => `/purchase-orders/${order.id}`,
      header: 'PO Number',
      sortable: true,
      render: (order) => (
        <div className="min-w-0">
          <div className="truncate font-semibold">{order.poNumber}</div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {order._count?.items || 0} items
          </div>
        </div>
      ),
    },
    {
      key: 'supplier',
      header: 'Supplier',
      render: (order) => (
        <span className="text-muted-foreground">{order.supplier?.name || '-'}</span>
      ),
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      render: (order) => (
        <span className="font-medium">{formatCurrency(order.totalAmount, order.currency)}</span>
      ),
    },
    {
      key: 'orderDate',
      header: 'Order Date',
      render: (order) => (
        <span className="text-muted-foreground">{formatDate(order.orderDate)}</span>
      ),
    },
    {
      key: 'expectedDate',
      header: 'Expected Date',
      render: (order) => (
        <span className="text-muted-foreground">{formatDate(order.expectedDate)}</span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (order) => <StatusBadge status={statusMap[order.status] || 'draft'} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (order) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {order.status === 'DRAFT' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setActionDialog({ open: true, order, action: 'submit' })}
                >
                  <Send className="mr-2 h-4 w-4" />
                  Submit for Approval
                </DropdownMenuItem>
              </>
            )}
            {order.status === 'APPROVED' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setActionDialog({ open: true, order, action: 'issue' })}
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Issue to Supplier
                </DropdownMenuItem>
              </>
            )}
            {order.status === 'DRAFT' && (
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => setActionDialog({ open: true, order, action: 'cancel' })}
              >
                <XCircle className="mr-2 h-4 w-4" />
                Cancel
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Purchase Orders"
          description="Orders raised on suppliers, from approval through to receipt against each line."
          actions={
            <Button onClick={() => navigate('/purchase-orders/new')}>
              <Plus className="mr-2 h-4 w-4" />
              Create PO
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <div className="mt-6">
        <KPIGrid columns={4}>
          <MetricCard
            label="Total Orders"
            value={orders.length}
            sentiment="info"
            icon={<FileText className="h-5 w-5" />}
          />
          <MetricCard label="Total Value" value={totalValue} sentiment="success" prefix="$" />
          <MetricCard
            label="Pending Approval"
            value={pendingOrders}
            sentiment={pendingOrders > 0 ? 'warning' : 'success'}
            trend={
              pendingOrders > 0
                ? { direction: 'up', value: '', text: 'needs attention' }
                : undefined
            }
            icon={<Package className="h-5 w-5" />}
          />
          <MetricCard label="In Progress" value={orderedCount} sentiment="neutral" />
        </KPIGrid>
      </div>

      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          toolbar={
            <TableFilters
              search={searchQuery}
              onSearchChange={setSearchQuery}
              searchPlaceholder="Search orders..."
              filters={[
                {
                  id: 'status',
                  label: 'Status',
                  value: statusFilter,
                  options: [
                    { value: 'DRAFT', label: 'Draft' },
                    { value: 'SUBMITTED', label: 'Pending Approval' },
                    { value: 'APPROVED', label: 'Approved' },
                    { value: 'ORDERED', label: 'Ordered' },
                    { value: 'PARTIALLY_RECEIVED', label: 'Partially Received' },
                    { value: 'RECEIVED', label: 'Received' },
                    { value: 'CANCELLED', label: 'Cancelled' },
                  ],
                  onChange: setStatusFilter,
                  placeholder: 'Status',
                },
              ]}
            />
          }
          data={orders}
          columns={columns}
          loading={loading}
          onRowClick={(order) => navigate(`/purchase-orders/${order.id}`)}
          emptyTitle="No purchase orders found"
          emptyDescription="Get started by creating your first purchase order."
          emptyAction={
            <Button onClick={openCreateForm}>
              <Plus className="mr-2 h-4 w-4" />
              Create PO
            </Button>
          }
          pagination
          exportable
          columnToggle
        />
      </motion.div>

      {/* Create PO Sheet */}
      <Sheet open={formOpen} onOpenChange={setFormOpen}>
        <SheetContent className="sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Create Purchase Order</SheetTitle>
            <SheetDescription>
              Select a supplier and add items to create a new purchase order.
            </SheetDescription>
          </SheetHeader>

          <form onSubmit={handleSubmit} className="space-y-6 mt-6">
            {/* Supplier Selection */}
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-muted-foreground">Order Details</h4>
              <div className="space-y-2">
                <Label htmlFor="supplier">Supplier *</Label>
                <Select
                  value={formData.supplierId}
                  onValueChange={(value) => setFormData({ ...formData, supplierId: value })}
                  disabled={loadingSuppliers}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={loadingSuppliers ? 'Loading...' : 'Select supplier'}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {suppliers.map((supplier) => (
                      <SelectItem key={supplier.id} value={supplier.id}>
                        {supplier.name} ({supplier.code})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="expectedDate">Expected Delivery</Label>
                  <Input
                    id="expectedDate"
                    type="date"
                    value={formData.expectedDate}
                    onChange={(e) => setFormData({ ...formData, expectedDate: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="currency">Currency</Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) => setFormData({ ...formData, currency: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>

            <Separator />

            {/* Line Items */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-muted-foreground">Line Items</h4>
                <Button type="button" variant="outline" size="sm" onClick={handleAddItem}>
                  <Plus className="mr-2 h-3 w-3" />
                  Add Item
                </Button>
              </div>

              {formData.items.length === 0 ? (
                <div className="rounded-lg border border-dashed p-6 text-center">
                  <Package className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">No items added yet.</p>
                  <Button type="button" variant="link" size="sm" onClick={handleAddItem}>
                    Add your first item
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {formData.items.map((item, index) => (
                    <div key={index} className="rounded-lg border p-3 space-y-3">
                      <div className="flex items-start justify-between">
                        <Label className="text-xs text-muted-foreground">Item {index + 1}</Label>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          aria-label="Remove"
                          className="h-6 w-6"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                      <div className="space-y-2">
                        <Select
                          value={item.itemId}
                          onValueChange={(value) => handleItemChange(index, 'itemId', value)}
                          disabled={loadingItems}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue
                              placeholder={loadingItems ? 'Loading...' : 'Select item'}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {inventoryItems.map((invItem) => (
                              <SelectItem key={invItem.id} value={invItem.id}>
                                {invItem.name} ({invItem.sku})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Quantity</Label>
                          <Input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) =>
                              handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)
                            }
                            className="h-9"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Unit Price ($)</Label>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={item.unitPrice}
                            onChange={(e) =>
                              handleItemChange(index, 'unitPrice', parseFloat(e.target.value) || 0)
                            }
                            className="h-9"
                          />
                        </div>
                      </div>
                      <div className="text-right text-sm font-medium">
                        Subtotal: {formatCurrency(item.quantity * item.unitPrice)}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {formData.items.length > 0 && (
                <div className="flex justify-between items-center pt-2 border-t">
                  <span className="font-medium">Total</span>
                  <span className="text-lg font-semibold">{formatCurrency(calculateTotal())}</span>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creating...' : 'Create Purchase Order'}
              </Button>
            </div>
          </form>
        </SheetContent>
      </Sheet>

      {/* Action Confirmation Dialog */}
      <ConfirmDialog
        open={actionDialog.open}
        onOpenChange={(open) => setActionDialog({ open })}
        {...getActionDialogContent()}
        onConfirm={handleAction}
        loading={submitMutation.isPending || issueMutation.isPending || cancelMutation.isPending}
      />
    </motion.main>
  );
}
